import type { MonthlyCallVolumePoint, ReportSeriesPoint, UserStatisticRow } from '@marnic/shared/reports';
import { TALK_TIME_BUCKETS, emptyTalkTime, talkTimeAtLeast } from '@marnic/shared';
import type React from 'react';
import { useState } from 'react';
import { formatDuration, formatShortDuration } from './report-utils';

const COLORS = {
  total: '#2563eb',
  inbound: '#10b981',
  outbound: '#f97316',
  answered: '#10b981',
  busy: '#ef4444',
  noAnswer: '#6366f1',
  avgDuration: '#8b5cf6',
  totalDuration: '#f59e0b',
  missed: '#ef4444',
  grid: '#e5e7eb',
};

const SOFT_COLORS = {
  answered: '#86efac',
  missed: '#fca5a5',
  weekday: '#60a5fa',
};

const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// Thresholds worth quoting in a tooltip: 1m+ upwards. "20s+" is noise next to
// the raw answered count, so the bands below a minute stay in the breakdown
// card where the full split is on show.
const TOOLTIP_THRESHOLDS = TALK_TIME_BUCKETS.filter((b) => b.minSeconds >= 60);

function thresholdLines(point: ReportSeriesPoint): TooltipLine[] {
  return TOOLTIP_THRESHOLDS.map((bucket) => ({
    label: `Talked ${bucket.atLeastLabel}`,
    value: talkTimeAtLeast(point.answeredByTalkTime, bucket.key),
    muted: true,
  }));
}

interface LineProps {
  title: string;
  data: ReportSeriesPoint[];
}

export function LineChart({ title, data }: LineProps): JSX.Element {
  const width = Math.max(720, data.length * 42);
  const height = 280;
  const max = Math.max(1, ...data.map((d) => Math.max(d.total, d.inbound, d.outbound)));
  const points = (key: 'total' | 'inbound' | 'outbound'): string =>
    data.map((d, i) => {
      const x = 40 + (i * (width - 80)) / Math.max(1, data.length - 1);
      const y = 20 + (1 - d[key] / max) * (height - 70);
      return `${x},${y}`;
    }).join(' ');
  const legend = (
    <ChartLegend items={[
      { color: COLORS.total, label: 'Total' },
      { color: COLORS.inbound, label: 'Inbound' },
      { color: COLORS.outbound, label: 'Outbound' },
    ]} />
  );
  return (
    <ChartFrame title={title} width={width} legend={legend}>
      <svg viewBox={`0 0 ${width} ${height}`} className="h-80 min-w-full">
        <Grid width={width} height={height} />
        <polyline fill="none" stroke={COLORS.total} strokeWidth="2.5" points={points('total')} />
        <polyline fill="none" stroke={COLORS.inbound} strokeWidth="2.5" points={points('inbound')} />
        <polyline fill="none" stroke={COLORS.outbound} strokeWidth="2.5" points={points('outbound')} />
        {data.map((d, i) => {
          const x = 40 + (i * (width - 80)) / Math.max(1, data.length - 1);
          return <text key={d.label} x={x} y={height - 10} textAnchor="end" transform={`rotate(-45 ${x} ${height - 10})`} className="fill-ink-500 text-[10px]">{d.label}</text>;
        })}
      </svg>
    </ChartFrame>
  );
}

export function UserDurationBars({ title, data, mode }: { title: string; data: UserStatisticRow[]; mode: 'average' | 'total' }): JSX.Element {
  const width = Math.max(560, data.length * 120);
  const height = 280;
  const values = data.map((d) => mode === 'average' ? d.averageDurationSeconds : d.totalDurationSeconds);
  const max = Math.max(1, ...values);
  const color = mode === 'average' ? COLORS.avgDuration : COLORS.totalDuration;
  return (
    <ChartFrame title={title} width={width}>
      <svg viewBox={`0 0 ${width} ${height}`} className="h-80 min-w-full">
        <Grid width={width} height={height} />
        {data.map((d, i) => {
          const value = values[i] ?? 0;
          const barHeight = (value / max) * (height - 80);
          const x = 70 + i * 110;
          const y = height - 45 - barHeight;
          return (
            <g key={d.user}>
              <line x1={x} x2={x} y1={height - 45} y2={y} stroke={color} strokeWidth="14" strokeLinecap="round" />
              <text x={x} y={y - 12} textAnchor="middle" className="fill-ink-600 text-xs">{formatDuration(value)}</text>
              <text x={x} y={height - 12} textAnchor="middle" className="fill-ink-600 text-xs font-semibold">{shortName(d.user)}</text>
            </g>
          );
        })}
      </svg>
    </ChartFrame>
  );
}

export function UserStackedBars({ title, data }: { title: string; data: UserStatisticRow[] }): JSX.Element {
  const width = Math.max(560, data.length * 120);
  const height = 310;
  const max = Math.max(1, ...data.map((d) => d.numberOfCalls));
  const legend = (
    <ChartLegend items={[
      { color: COLORS.answered, label: 'Answered' },
      { color: COLORS.missed, label: 'Missed' },
    ]} />
  );
  return (
    <ChartFrame title={title} width={width} legend={legend}>
      <svg viewBox={`0 0 ${width} ${height}`} className="h-80 min-w-full">
        <Grid width={width} height={height} />
        {data.map((d, i) => {
          const answered = (d.answered / max) * (height - 90);
          const missed = (d.missedOrNotAnswered / max) * (height - 90);
          const x = 70 + i * 110;
          const base = height - 55;
          return (
            <g key={d.user}>
              <line x1={x} x2={x} y1={base} y2={base - answered} stroke={COLORS.answered} strokeWidth="14" strokeLinecap="round" />
              <line x1={x} x2={x} y1={base - answered} y2={base - answered - missed} stroke={COLORS.missed} strokeWidth="14" strokeLinecap="round" />
              <text x={x} y={height - 18} textAnchor="middle" className="fill-ink-600 text-xs font-semibold">{shortName(d.user)}</text>
            </g>
          );
        })}
      </svg>
    </ChartFrame>
  );
}

export function MonthlyVolumeChart({ title, data }: { title: string; data: MonthlyCallVolumePoint[] }): JSX.Element {
  const width = Math.max(760, data.length * 170);
  const height = 250;
  const max = Math.max(1, ...data.map((d) => d.answered + d.busy + d.noAnswer));
  return (
    <ChartFrame title={title} width={width}>
      <svg viewBox={`0 0 ${width} ${height}`} className="h-72 min-w-full">
        <Grid width={width} height={height} />
        {data.map((d, i) => {
          const x = 80 + i * ((width - 140) / Math.max(1, data.length - 1));
          const answered = (d.answered / max) * (height - 80);
          const busy = (d.busy / max) * (height - 80);
          const noAnswer = (d.noAnswer / max) * (height - 80);
          const base = height - 42;
          return (
            <g key={d.month}>
              <line x1={x} x2={x} y1={base} y2={base - answered} stroke={COLORS.answered} strokeWidth="12" strokeLinecap="round" />
              <line x1={x} x2={x} y1={base - answered} y2={base - answered - busy} stroke={COLORS.busy} strokeWidth="12" strokeLinecap="butt" />
              <line x1={x} x2={x} y1={base - answered - busy} y2={base - answered - busy - noAnswer} stroke={COLORS.noAnswer} strokeWidth="12" strokeLinecap="round" />
              <text x={x} y={height - 12} textAnchor="middle" className="fill-ink-600 text-xs">{d.month}</text>
            </g>
          );
        })}
      </svg>
    </ChartFrame>
  );
}

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function formatDailyLabel(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${String(d.getUTCDate()).padStart(2, '0')} ${MONTH_LABELS[d.getUTCMonth()]}`;
}

export function DailyOverviewChart({ title, data }: { title: string; data: ReportSeriesPoint[] }): JSX.Element {
  const [hover, setHover] = useState<number | null>(null);
  const width = 1200;
  const height = 360;
  const padLeft = 56;
  const padRight = 24;
  const padTop = 24;
  const padBottom = 68;
  const innerWidth = width - padLeft - padRight;
  const innerHeight = height - padTop - padBottom;
  const points = data.length;
  const step = points > 1 ? innerWidth / (points - 1) : innerWidth;
  const peak = Math.max(5, ...data.map((d) => Math.max(d.total, d.answered, d.missedOrNotAnswered)));
  const scale = niceScale(peak);
  const niceMax = scale.max;
  const ticks = Math.round(niceMax / scale.step);
  const pointX = (i: number): number => (points === 1 ? padLeft + innerWidth / 2 : padLeft + step * i);
  const pointY = (val: number): number => padTop + innerHeight * (1 - val / niceMax);
  const linePath = (key: 'total' | 'answered' | 'missedOrNotAnswered'): string =>
    data.map((d, i) => `${i === 0 ? 'M' : 'L'} ${pointX(i)} ${pointY(d[key])}`).join(' ');
  const legend = (
    <ChartLegend items={[
      { color: '#3b82f6', label: 'Total calls' },
      { color: SOFT_COLORS.answered, label: 'Answered' },
      { color: SOFT_COLORS.missed, label: 'Missed/Not answered' },
    ]} />
  );
  const labelStride = points <= 14 ? 1 : points <= 31 ? 2 : Math.ceil(points / 14);
  const hoveredCx = hover !== null ? pointX(hover) : 0;
  const hoveredTopY = hover !== null
    ? Math.min(pointY(data[hover]!.total), pointY(data[hover]!.answered), pointY(data[hover]!.missedOrNotAnswered))
    : 0;
  if (points === 0) {
    return (
      <ResponsiveChart title={title} legend={legend}>
        <div className="flex items-center justify-center px-3 py-12 text-sm text-ink-500">No call data for this range yet.</div>
      </ResponsiveChart>
    );
  }
  return (
    <ResponsiveChart title={title} legend={legend}>
      <svg viewBox={`0 0 ${width} ${height}`} className="block w-full" style={{ aspectRatio: `${width} / ${height}` }} preserveAspectRatio="xMidYMid meet" onMouseLeave={() => setHover(null)}>
        {Array.from({ length: ticks + 1 }, (_, i) => {
          const value = (niceMax / ticks) * i;
          const y = padTop + innerHeight * (1 - value / niceMax);
          return (
            <g key={i}>
              <line x1={padLeft} x2={width - padRight} y1={y} y2={y} stroke={COLORS.grid} strokeDasharray="3 6" />
              <text x={padLeft - 8} y={y + 4} textAnchor="end" className="fill-ink-500 text-[14px]">{Math.round(value)}</text>
            </g>
          );
        })}
        <path d={linePath('total')} fill="none" stroke="#3b82f6" strokeWidth={3} strokeLinejoin="round" />
        <path d={linePath('answered')} fill="none" stroke={SOFT_COLORS.answered} strokeWidth={3} strokeLinejoin="round" />
        <path d={linePath('missedOrNotAnswered')} fill="none" stroke={SOFT_COLORS.missed} strokeWidth={3} strokeLinejoin="round" />
        {data.map((d, i) => (
          <g key={`dots-${i}`}>
            <circle cx={pointX(i)} cy={pointY(d.total)} r={5} fill="#3b82f6" />
            <circle cx={pointX(i)} cy={pointY(d.answered)} r={5} fill={SOFT_COLORS.answered} />
            <circle cx={pointX(i)} cy={pointY(d.missedOrNotAnswered)} r={5} fill={SOFT_COLORS.missed} />
          </g>
        ))}
        {data.map((d, i) => {
          if (i % labelStride !== 0 && i !== points - 1) return null;
          const x = pointX(i);
          return (
            <text key={`label-${i}`} x={x} y={height - 36} textAnchor="end" transform={`rotate(-30 ${x} ${height - 36})`} className="fill-ink-500 text-[14px]">
              {formatDailyLabel(d.label)}
            </text>
          );
        })}
        {hover !== null && (
          <line x1={hoveredCx} x2={hoveredCx} y1={padTop} y2={padTop + innerHeight} stroke="#475569" strokeWidth={1} strokeDasharray="4 4" pointerEvents="none" />
        )}
        {data.map((_, i) => (
          <rect
            key={`hit-${i}`}
            x={pointX(i) - Math.max(step / 2, 6)}
            y={padTop}
            width={Math.max(step, 12)}
            height={innerHeight}
            fill="transparent"
            onMouseEnter={() => setHover(i)}
            style={{ cursor: 'pointer' }}
          />
        ))}
        {hover !== null && (
          <SvgTooltip
            x={hoveredCx}
            y={hoveredTopY}
            chartWidth={width}
            chartHeight={height}
            padTop={padTop}
            lines={[
              { label: formatDailyLabel(data[hover]!.label), bold: true },
              { swatch: '#3b82f6', label: 'Total calls', value: data[hover]!.total },
              { swatch: SOFT_COLORS.answered, label: 'Answered', value: data[hover]!.answered },
              ...thresholdLines(data[hover]!),
              { swatch: SOFT_COLORS.missed, label: 'Missed/Not answered', value: data[hover]!.missedOrNotAnswered },
              { label: 'Talk time', value: formatShortDuration(data[hover]!.answeredDurationSeconds), muted: true },
            ]}
          />
        )}
      </svg>
    </ResponsiveChart>
  );
}

// Talk time needs its own chart rather than a second series on the call-count
// charts: seconds and call counts share no axis, and overlaying them makes both
// unreadable. Works for any series — daily, hourly, or per queue/ring group —
// because formatDailyLabel passes non-date labels straight through.
export function TalkTimeBars({ title, data }: { title: string; data: ReportSeriesPoint[] }): JSX.Element {
  const [hover, setHover] = useState<number | null>(null);
  const width = 1200;
  const height = 320;
  const padLeft = 86;
  const padRight = 24;
  const padTop = 24;
  const padBottom = 68;
  const innerWidth = width - padLeft - padRight;
  const innerHeight = height - padTop - padBottom;
  const points = data.length;
  const step = innerWidth / Math.max(1, points);
  const peak = Math.max(...data.map((d) => d.answeredDurationSeconds), 0);
  const scale = niceDurationScale(peak);
  const ticks = Math.round(scale.max / scale.step);
  const barWidth = Math.min(28, Math.max(4, step * 0.6));
  const labelStride = points <= 14 ? 1 : points <= 31 ? 2 : Math.ceil(points / 14);
  const legend = <ChartLegend items={[{ color: COLORS.totalDuration, label: 'Answered talk time' }]} />;
  const hoveredCx = hover !== null ? padLeft + step * (hover + 0.5) : 0;
  const hoveredTopY = hover !== null
    ? padTop + innerHeight - (data[hover]!.answeredDurationSeconds / scale.max) * innerHeight
    : 0;
  if (points === 0) {
    return (
      <ResponsiveChart title={title} legend={legend}>
        <div className="flex items-center justify-center px-3 py-12 text-sm text-ink-500">No call data for this range yet.</div>
      </ResponsiveChart>
    );
  }
  return (
    <ResponsiveChart title={title} legend={legend}>
      <svg viewBox={`0 0 ${width} ${height}`} className="block w-full" style={{ aspectRatio: `${width} / ${height}` }} preserveAspectRatio="xMidYMid meet" onMouseLeave={() => setHover(null)}>
        {Array.from({ length: ticks + 1 }, (_, i) => {
          const value = scale.step * i;
          const y = padTop + innerHeight * (1 - value / scale.max);
          return (
            <g key={i}>
              <line x1={padLeft} x2={width - padRight} y1={y} y2={y} stroke={COLORS.grid} strokeDasharray="3 6" />
              <text x={padLeft - 8} y={y + 4} textAnchor="end" className="fill-ink-500 text-[14px]">{formatShortDuration(value)}</text>
            </g>
          );
        })}
        {data.map((d, i) => {
          const cx = padLeft + step * (i + 0.5);
          const baseY = padTop + innerHeight;
          const h = (d.answeredDurationSeconds / scale.max) * innerHeight;
          return (
            <g key={`bar-${i}`}>
              {h > 0 && (
                <line x1={cx} x2={cx} y1={baseY} y2={baseY - h} stroke={COLORS.totalDuration} strokeWidth={barWidth} strokeLinecap="round" />
              )}
            </g>
          );
        })}
        {data.map((d, i) => {
          if (i % labelStride !== 0 && i !== points - 1) return null;
          const cx = padLeft + step * (i + 0.5);
          return (
            <text key={`label-${i}`} x={cx} y={height - 36} textAnchor="end" transform={`rotate(-30 ${cx} ${height - 36})`} className="fill-ink-500 text-[14px]">
              {formatDailyLabel(d.label)}
            </text>
          );
        })}
        {data.map((_, i) => {
          const cx = padLeft + step * (i + 0.5);
          return (
            <rect
              key={`hit-${i}`}
              x={cx - step / 2}
              y={padTop}
              width={step}
              height={innerHeight}
              fill="transparent"
              onMouseEnter={() => setHover(i)}
              style={{ cursor: 'pointer' }}
            />
          );
        })}
        {hover !== null && (
          <line x1={hoveredCx} x2={hoveredCx} y1={padTop} y2={padTop + innerHeight} stroke="#475569" strokeWidth={1} strokeDasharray="4 4" pointerEvents="none" />
        )}
        {hover !== null && (
          <SvgTooltip
            x={hoveredCx}
            y={hoveredTopY}
            chartWidth={width}
            chartHeight={height}
            padTop={padTop}
            lines={[
              { label: formatDailyLabel(data[hover]!.label), bold: true },
              { swatch: COLORS.totalDuration, label: 'Talk time', value: formatShortDuration(data[hover]!.answeredDurationSeconds) },
              { swatch: SOFT_COLORS.answered, label: 'Answered', value: data[hover]!.answered },
              { label: 'Avg per answered', value: formatDuration(averagePerAnswered(data[hover]!)), muted: true },
            ]}
          />
        )}
      </svg>
    </ResponsiveChart>
  );
}

function averagePerAnswered(point: ReportSeriesPoint): number {
  return point.answered ? Math.round(point.answeredDurationSeconds / point.answered) : 0;
}

// Duration axes want human step sizes (5m, 30m, 2h), not the decimal 1/2/5
// ladder niceScale produces — a "2500 seconds" gridline tells nobody anything.
const DURATION_STEPS = [15, 30, 60, 120, 300, 600, 900, 1800, 3600, 7200, 10800, 21600, 43200, 86400];

function niceDurationScale(peak: number): { max: number; step: number } {
  const target = Math.max(1, peak) / 5;
  const step = DURATION_STEPS.find((s) => s >= target) ?? DURATION_STEPS[DURATION_STEPS.length - 1]!;
  return { max: Math.max(step, Math.ceil(peak / step) * step), step };
}

export function HourlyStackedBars({ title, data }: { title: string; data: ReportSeriesPoint[] }): JSX.Element {
  const [hover, setHover] = useState<number | null>(null);
  const hours = Array.from({ length: 24 }, (_, h) => data[h] ?? ({ answered: 0, answeredByTalkTime: emptyTalkTime(), answeredDurationSeconds: 0, missedOrNotAnswered: 0, total: 0 } as ReportSeriesPoint));
  const width = 880;
  const height = 320;
  const padLeft = 50;
  const padRight = 18;
  const padTop = 24;
  const padBottom = 72;
  const innerWidth = width - padLeft - padRight;
  const innerHeight = height - padTop - padBottom;
  const step = innerWidth / 24;
  const peak = Math.max(5, ...hours.map((d) => d.answered + d.missedOrNotAnswered));
  const scale = niceScale(peak);
  const niceMax = scale.max;
  const ticks = Math.round(niceMax / scale.step);
  const legend = (
    <ChartLegend items={[
      { color: SOFT_COLORS.answered, label: 'Answered' },
      { color: SOFT_COLORS.missed, label: 'Missed/Not answered' },
    ]} />
  );
  const hoveredCx = hover !== null ? padLeft + step * (hover + 0.5) : 0;
  const hoveredTopY = hover !== null
    ? padTop + innerHeight - ((hours[hover]!.answered + hours[hover]!.missedOrNotAnswered) / niceMax) * innerHeight
    : 0;
  return (
    <ResponsiveChart title={title} legend={legend}>
      <svg viewBox={`0 0 ${width} ${height}`} className="block w-full" style={{ aspectRatio: `${width} / ${height}` }} preserveAspectRatio="xMidYMid meet" onMouseLeave={() => setHover(null)}>
        {Array.from({ length: ticks + 1 }, (_, i) => {
          const value = (niceMax / ticks) * i;
          const y = padTop + innerHeight * (1 - value / niceMax);
          return (
            <g key={i}>
              <line x1={padLeft} x2={width - padRight} y1={y} y2={y} stroke={COLORS.grid} strokeDasharray="3 6" />
              <text x={padLeft - 8} y={y + 4} textAnchor="end" className="fill-ink-500 text-[14px]">{Math.round(value)}</text>
            </g>
          );
        })}
        {hours.map((d, i) => {
          const cx = padLeft + step * (i + 0.5);
          const baseY = padTop + innerHeight;
          const answeredH = (d.answered / niceMax) * innerHeight;
          const missedH = (d.missedOrNotAnswered / niceMax) * innerHeight;
          const stroke = 18;
          return (
            <g key={i}>
              {answeredH > 0 && (
                <line x1={cx} x2={cx} y1={baseY} y2={baseY - answeredH} stroke={SOFT_COLORS.answered} strokeWidth={stroke} strokeLinecap="round" />
              )}
              {missedH > 0 && (
                <line x1={cx} x2={cx} y1={baseY - answeredH - (answeredH > 0 ? 2 : 0)} y2={baseY - answeredH - missedH} stroke={SOFT_COLORS.missed} strokeWidth={stroke} strokeLinecap="round" />
              )}
              <text x={cx} y={height - 46} textAnchor="end" transform={`rotate(-45 ${cx} ${height - 46})`} className="fill-ink-500 text-[14px]">{hourLabel(i)}</text>
            </g>
          );
        })}
        {hours.map((_, i) => {
          const cx = padLeft + step * (i + 0.5);
          return (
            <rect
              key={`hit-${i}`}
              x={cx - step / 2}
              y={padTop}
              width={step}
              height={innerHeight}
              fill="transparent"
              onMouseEnter={() => setHover(i)}
              style={{ cursor: 'pointer' }}
            />
          );
        })}
        {hover !== null && (
          <line x1={hoveredCx} x2={hoveredCx} y1={padTop} y2={padTop + innerHeight} stroke="#475569" strokeWidth={1} strokeDasharray="4 4" pointerEvents="none" />
        )}
        {hover !== null && (
          <SvgTooltip
            x={hoveredCx}
            y={hoveredTopY}
            chartWidth={width}
            chartHeight={height}
            padTop={padTop}
            lines={[
              { label: hourLabel(hover), bold: true },
              { swatch: '#3b82f6', label: 'Total calls', value: hours[hover]!.answered + hours[hover]!.missedOrNotAnswered },
              { swatch: SOFT_COLORS.answered, label: 'Answered', value: hours[hover]!.answered },
              ...thresholdLines(hours[hover]!),
              { swatch: SOFT_COLORS.missed, label: 'Missed/Not answered', value: hours[hover]!.missedOrNotAnswered },
              { label: 'Talk time', value: formatShortDuration(hours[hover]!.answeredDurationSeconds), muted: true },
            ]}
          />
        )}
      </svg>
    </ResponsiveChart>
  );
}

export function WeekdayBars({ title, data }: { title: string; data: ReportSeriesPoint[] }): JSX.Element {
  const [hover, setHover] = useState<number | null>(null);
  const weekly = computeWeekday(data);
  const width = 420;
  const height = 300;
  const padLeft = 50;
  const padRight = 14;
  const padTop = 32;
  const padBottom = 44;
  const innerWidth = width - padLeft - padRight;
  const innerHeight = height - padTop - padBottom;
  const step = innerWidth / 7;
  const peak = Math.max(5, ...weekly.map((d) => d.total));
  const scale = niceScale(peak);
  const niceMax = scale.max;
  const ticks = Math.round(niceMax / scale.step);
  const hoveredCx = hover !== null ? padLeft + step * (hover + 0.5) : 0;
  const hoveredTopY = hover !== null
    ? padTop + innerHeight - (weekly[hover]!.total / niceMax) * innerHeight
    : 0;
  return (
    <ResponsiveChart title={title}>
      <svg viewBox={`0 0 ${width} ${height}`} className="block w-full" style={{ aspectRatio: `${width} / ${height}` }} preserveAspectRatio="xMidYMid meet" onMouseLeave={() => setHover(null)}>
        {Array.from({ length: ticks + 1 }, (_, i) => {
          const value = (niceMax / ticks) * i;
          const y = padTop + innerHeight * (1 - value / niceMax);
          return (
            <g key={i}>
              <line x1={padLeft} x2={width - padRight} y1={y} y2={y} stroke={COLORS.grid} strokeDasharray="3 6" />
              <text x={padLeft - 8} y={y + 4} textAnchor="end" className="fill-ink-500 text-[14px]">{Math.round(value)}</text>
            </g>
          );
        })}
        {weekly.map((d, i) => {
          const cx = padLeft + step * (i + 0.5);
          const baseY = padTop + innerHeight;
          const h = (d.total / niceMax) * innerHeight;
          return (
            <g key={d.label}>
              {h > 0 && (
                <line x1={cx} x2={cx} y1={baseY} y2={baseY - h} stroke={SOFT_COLORS.weekday} strokeWidth={28} strokeLinecap="round" />
              )}
              <text x={cx} y={baseY - h - 10} textAnchor="middle" className="fill-ink-700 text-[16px] font-semibold">{d.total}</text>
              <text x={cx} y={height - 10} textAnchor="middle" className="fill-ink-500 text-[16px]">{d.label}</text>
            </g>
          );
        })}
        {weekly.map((_, i) => {
          const cx = padLeft + step * (i + 0.5);
          return (
            <rect
              key={`hit-${i}`}
              x={cx - step / 2}
              y={padTop}
              width={step}
              height={innerHeight}
              fill="transparent"
              onMouseEnter={() => setHover(i)}
              style={{ cursor: 'pointer' }}
            />
          );
        })}
        {hover !== null && (
          <line x1={hoveredCx} x2={hoveredCx} y1={padTop} y2={padTop + innerHeight} stroke="#475569" strokeWidth={1} strokeDasharray="4 4" pointerEvents="none" />
        )}
        {hover !== null && (
          <SvgTooltip
            x={hoveredCx}
            y={hoveredTopY}
            chartWidth={width}
            chartHeight={height}
            padTop={padTop}
            lines={[
              { label: weekdayFullName(hover), bold: true },
              { swatch: SOFT_COLORS.weekday, label: 'Calls', value: weekly[hover]!.total },
              { swatch: SOFT_COLORS.answered, label: 'Answered', value: weekly[hover]!.answered },
              { swatch: COLORS.totalDuration, label: 'Talk time', value: formatShortDuration(weekly[hover]!.talkSeconds) },
            ]}
          />
        )}
      </svg>
    </ResponsiveChart>
  );
}

function ResponsiveChart({ title, legend, children }: { title: string; legend?: React.ReactNode; children: React.ReactNode }): JSX.Element {
  return (
    <section className="card flex h-full flex-col overflow-hidden">
      <h2 className="px-3 pt-3 text-sm font-semibold text-ink-700">{title}</h2>
      <div className="flex-1 px-3 pb-2 pt-1">{children}</div>
      {legend}
    </section>
  );
}

function hourLabel(hour: number): string {
  return `${String(hour).padStart(2, '0')}:00`;
}

const WEEKDAY_FULL = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
function weekdayFullName(index: number): string {
  return WEEKDAY_FULL[index] ?? '';
}

interface TooltipLine {
  label: string;
  value?: number | string;
  swatch?: string;
  bold?: boolean;
  muted?: boolean;
}

function SvgTooltip({ x, y, chartWidth, chartHeight, padTop, lines }: { x: number; y: number; chartWidth: number; chartHeight: number; padTop: number; lines: TooltipLine[] }): JSX.Element {
  const padX = 12;
  const padY = 10;
  const lineHeight = 22;
  const boxWidth = 230;
  const boxHeight = padY * 2 + lines.length * lineHeight;
  const preferredX = x + 16;
  const minX = 4;
  const maxX = chartWidth - boxWidth - 4;
  const fitsRight = preferredX <= maxX;
  const boxX = fitsRight ? preferredX : Math.max(minX, x - boxWidth - 16);
  const preferredY = y - boxHeight - 8;
  const boxY = preferredY < padTop + 2 ? Math.min(chartHeight - boxHeight - 4, y + 16) : preferredY;
  const swatchSize = 12;
  return (
    <g pointerEvents="none">
      <rect x={boxX} y={boxY} width={boxWidth} height={boxHeight} rx={6} fill="#1f2937" opacity={0.96} />
      {lines.map((line, i) => {
        const rowY = boxY + padY + i * lineHeight + 16;
        const textX = boxX + padX + (line.swatch ? swatchSize + 8 : 0);
        return (
          <g key={i}>
            {line.swatch && (
              <rect x={boxX + padX} y={rowY - swatchSize - 1} width={swatchSize} height={swatchSize} rx={2} fill={line.swatch} />
            )}
            <text x={textX} y={rowY} className={`${line.muted ? 'fill-ink-300' : 'fill-white'} ${line.bold ? 'font-semibold text-[16px]' : 'text-[14px]'}`}>
              {line.label}
              {line.value !== undefined && (
                <tspan dx={4}>: {line.value}</tspan>
              )}
            </text>
          </g>
        );
      })}
    </g>
  );
}

function niceCeil(value: number): number {
  return niceScale(value).max;
}

function niceScale(value: number): { max: number; step: number } {
  if (value <= 5) return { max: 5, step: 1 };
  const targetStep = value / 5;
  const magnitude = Math.pow(10, Math.floor(Math.log10(targetStep)));
  const normalized = targetStep / magnitude;
  const step = (normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 2.5 ? 2.5 : normalized <= 5 ? 5 : 10) * magnitude;
  return { max: Math.ceil(value / step) * step, step };
}

function computeWeekday(daily: ReportSeriesPoint[]): { label: string; total: number; answered: number; talkSeconds: number }[] {
  const totals = [0, 0, 0, 0, 0, 0, 0];
  const answered = [0, 0, 0, 0, 0, 0, 0];
  const talk = [0, 0, 0, 0, 0, 0, 0];
  for (const point of daily) {
    const date = new Date(point.label);
    if (Number.isNaN(date.getTime())) continue;
    const dow = date.getUTCDay();
    totals[dow]! += point.total;
    answered[dow]! += point.answered;
    talk[dow]! += point.answeredDurationSeconds;
  }
  return WEEKDAY_LABELS.map((label, i) => ({ label, total: totals[i]!, answered: answered[i]!, talkSeconds: talk[i]! }));
}

export const monthlyVolumeColors = {
  answered: COLORS.answered,
  busy: COLORS.busy,
  noAnswer: COLORS.noAnswer,
};

export function ChartLegend({ items }: { items: { color: string; label: string }[] }): JSX.Element {
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 px-3 pb-3 text-xs text-ink-600">
      {items.map((item) => (
        <span key={item.label} className="inline-flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: item.color }} />
          {item.label}
        </span>
      ))}
    </div>
  );
}

function ChartFrame({ title, width, legend, children }: { title: string; width: number; legend?: React.ReactNode; children: React.ReactNode }): JSX.Element {
  return (
    <section className="card overflow-hidden">
      <h2 className="px-3 pt-3 text-sm font-semibold text-ink-700">{title}</h2>
      <div className="overflow-x-auto" style={{ minWidth: 0 }}>
        <div style={{ width }}>{children}</div>
      </div>
      {legend}
    </section>
  );
}

function Grid({ width, height }: { width: number; height: number }): JSX.Element {
  return (
    <g>
      {Array.from({ length: 8 }, (_, i) => {
        const y = 20 + i * ((height - 70) / 7);
        return <line key={i} x1="40" x2={width - 20} y1={y} y2={y} stroke={COLORS.grid} strokeDasharray="3 6" />;
      })}
    </g>
  );
}

function shortName(value: string): string {
  return value.replace(/^\d+\s*-\s*/, '').split(' ')[0] ?? value;
}
